import * as React from "react";
import { Tooltip as AtlasTooltip } from "@wellsky/atlas-ui-react";
import { PortalContext } from "../../widget/WidgetProvider";

// Placement mapping from Radix side/sideOffset to atlas-ui placement/offset
type RadixSide = "top" | "bottom" | "left" | "right";

interface TooltipContentProps {
  children: React.ReactNode;
  side?: RadixSide;
  sideOffset?: number;
  className?: string;
}

interface TooltipComposableProps {
  children: React.ReactNode;
}

// Store tooltip content via context to bridge composable → single-component
const TooltipContentContext = React.createContext<{
  content: React.ReactNode;
  placement: RadixSide;
  offset: number;
}>({ content: null, placement: "top", offset: 0 });

function TooltipProvider({ children }: { children: React.ReactNode; delayDuration?: number }) {
  return <>{children}</>;
}

function TooltipContent({ children, side = "top", sideOffset = 0 }: TooltipContentProps) {
  // This component is collected by the Tooltip wrapper, not rendered directly
  return (
    <TooltipContentContext.Provider value={{ content: children, placement: side, offset: sideOffset }}>
      {null}
    </TooltipContentContext.Provider>
  );
}

function TooltipTrigger({ children }: { children: React.ReactNode; asChild?: boolean }) {
  // Just pass through children — atlas-ui Tooltip wraps the trigger directly
  return <>{children}</>;
}

/**
 * Tooltip wrapper that bridges the composable (Radix) API to atlas-ui's single-component API.
 *
 * Supports both patterns:
 * 1. Composable (backward compat): <Tooltip><TooltipTrigger>...</TooltipTrigger><TooltipContent>text</TooltipContent></Tooltip>
 * 2. Direct atlas-ui: <Tooltip content="text"><trigger /></Tooltip>
 */
function Tooltip({ children, ...props }: TooltipComposableProps & Record<string, unknown>) {
  const portalContainer = React.useContext(PortalContext);

  // If called with direct atlas-ui props (content prop), pass through
  if ("content" in props) {
    const { content, placement, offset, container, ...rest } = props as {
      content: React.ReactNode;
      placement?: RadixSide;
      offset?: number;
      container?: HTMLElement;
    };
    return (
      <AtlasTooltip
        content={content}
        placement={placement}
        offset={offset}
        container={container || (portalContainer as HTMLElement | undefined)}
        {...rest}
      >
        {children as React.ReactElement}
      </AtlasTooltip>
    );
  }

  // Composable pattern: extract trigger and content from children
  let triggerElement: React.ReactNode = null;
  let contentText: React.ReactNode = null;
  let placement: RadixSide = "top";
  let offset = 0;

  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return;

    const childType = child.type as React.ComponentType;
    if (childType === TooltipTrigger) {
      // Extract the actual trigger element from TooltipTrigger's children
      triggerElement = child.props.children;
    } else if (childType === TooltipContent) {
      contentText = child.props.children;
      placement = child.props.side ?? "top";
      offset = child.props.sideOffset ?? 0;
    }
  });

  if (!triggerElement || !React.isValidElement(triggerElement)) {
    // Fallback: render children as-is
    return <>{children}</>;
  }

  return (
    <AtlasTooltip
      content={contentText}
      placement={placement}
      offset={offset}
      container={portalContainer as HTMLElement | undefined}
    >
      {triggerElement}
    </AtlasTooltip>
  );
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
